import { useMemo } from 'react';
import { TeamContainer, UserContainer } from '@/providers';
import { ConnectionQuery } from '@/queries/connection';
import { useSession } from '@/states/common';

interface PlanLimits {
  datasources: number | 'unlimited';
  workspaces: number | 'unlimited';
  users: number | 'unlimited';
}

interface CurrentCounts {
  datasources: number;
  workspaces: number;
  users: number;
}

interface LimitReachedFlags {
  datasources: boolean;
  workspaces: boolean;
  users: boolean;
}

interface ExceededFlags {
  datasources: boolean;
  workspaces: boolean;
  users: boolean;
}

interface UsePlanLimitsReturn {
  limits: PlanLimits;
  current: CurrentCounts;
  limitReached: LimitReachedFlags;
  exceeded: ExceededFlags;
  isOverLimit: boolean;
  planName: string;
  isTenCodePurchaser: boolean;
}

/**
 * Unified hook for plan limits checking and enforcement.
 * Use this hook when you need to check if current usage exceeds plan limits.
 *
 * @returns {UsePlanLimitsReturn} Object containing limits, current counts, exceeded flags, and plan info
 */
export const usePlanLimits = (): UsePlanLimitsReturn => {
  const { data: session } = useSession();
  const { state: teamState } = TeamContainer.useContainer();

  const { pages: dataSources } = session;

  return useMemo(() => {
    const billing = session?.business?.billing;

    // Get limits from billing info
    // Note: sources and workspaces can be number | boolean | 'unlimited'
    // team can be number | 'unlimited' (and possibly boolean in practice)
    const sourcesLimit = billing?.sources ?? 1;
    const workspacesLimit = billing?.workspaces ?? 1;
    const usersLimit = billing?.team ?? 1;

    // Handle legacy AppSumo/TenCode users with additional resources
    const totalSources = sourcesLimit + (billing?.additionalSources ?? 0);
    const additionalUsers = billing?.additionalUsers ?? 0;

    // TenCode purchasers have unlimited workspaces
    const isTenCodePurchaser = billing?.isTenCodePurchaser;

    // Calculate final limits - handle 'unlimited' string, boolean values, and numeric values
    // Boolean true indicates unlimited access, boolean false indicates limit of 1
    let datasourcesLimit: number | 'unlimited';
    if (
      sourcesLimit === 'unlimited' ||
      sourcesLimit === true ||
      (typeof totalSources === 'number' && totalSources >= 999)
    ) {
      datasourcesLimit = 'unlimited';
    } else if (sourcesLimit === false) {
      datasourcesLimit = 1;
    } else {
      datasourcesLimit = totalSources as number;
    }

    let finalWorkspacesLimit: number | 'unlimited';
    if (
      isTenCodePurchaser ||
      workspacesLimit === 'unlimited' ||
      workspacesLimit === true ||
      (typeof workspacesLimit === 'number' && workspacesLimit >= 999)
    ) {
      finalWorkspacesLimit = 'unlimited';
    } else if (workspacesLimit === false) {
      finalWorkspacesLimit = 1;
    } else {
      finalWorkspacesLimit = workspacesLimit as number;
    }

    let finalUsersLimit: number | 'unlimited';
    if (
      usersLimit === 'unlimited' ||
      usersLimit === true ||
      (typeof usersLimit === 'number' && usersLimit >= 999)
    ) {
      finalUsersLimit = 'unlimited';
    } else if (additionalUsers > 0) {
      finalUsersLimit = additionalUsers + 1;
    } else if (usersLimit === false) {
      finalUsersLimit = 1;
    } else {
      finalUsersLimit = usersLimit as number;
    }

    // Get current counts
    const currentDatasources =
      dataSources?.filter((ds: any) => ds.clientId)?.length ?? 0;
    const currentWorkspaces = session?.clients?.length ?? 1;
    const currentUsers = teamState?.members?.length ?? 1;

    // Calculate limit reached flags (at or over limit = can't add more)
    const datasourcesLimitReached =
      datasourcesLimit === 'unlimited'
        ? false
        : currentDatasources >= datasourcesLimit;

    const workspacesLimitReached =
      finalWorkspacesLimit === 'unlimited'
        ? false
        : currentWorkspaces >= finalWorkspacesLimit;

    const usersLimitReached =
      finalUsersLimit === 'unlimited' ? false : currentUsers >= finalUsersLimit;

    // Calculate exceeded flags (strictly over limit = violation)
    const datasourcesExceeded =
      datasourcesLimit === 'unlimited'
        ? false
        : currentDatasources > datasourcesLimit;

    const workspacesExceeded =
      finalWorkspacesLimit === 'unlimited'
        ? false
        : currentWorkspaces > finalWorkspacesLimit;

    const usersExceeded =
      finalUsersLimit === 'unlimited' ? false : currentUsers > finalUsersLimit;

    // Overall exceeded state
    const isOverLimit =
      datasourcesExceeded || workspacesExceeded || usersExceeded;

    // Get plan name
    const subscriptionName = billing?.paddleSubscription?.planName ?? '';
    const planName =
      subscriptionName?.split('-')?.[0].split(' ')?.[0]?.toLowerCase() ??
      'free';

    return {
      limits: {
        datasources: datasourcesLimit,
        workspaces: finalWorkspacesLimit,
        users: finalUsersLimit,
      },
      current: {
        datasources: currentDatasources,
        workspaces: currentWorkspaces,
        users: currentUsers,
      },
      limitReached: {
        datasources: datasourcesLimitReached,
        workspaces: workspacesLimitReached,
        users: usersLimitReached,
      },
      exceeded: {
        datasources: datasourcesExceeded,
        workspaces: workspacesExceeded,
        users: usersExceeded,
      },
      isOverLimit,
      planName,
      isTenCodePurchaser,
    };
  }, [
    session?.business?.billing,
    session?.clients,
    teamState?.members,
    dataSources,
  ]);
};
